import _ from 'lodash'
import Backbone from 'backbone'
import Nav from 'scoreboard/components/nav'
import NewTab from 'scoreboard/components/new-tab'
import Form from 'scoreboard/forms/model/form'
import FormsGardener from './forms-gardener'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'
import permissions from 'scoreboard/components/permissions'
import TreeActions from 'scoreboard/components/tree-actions'
import FormType from './model/form-type'
const { FORM, FOLDER } = FormType
import Tooltip from 'scoreboard/components/tooltip'
import AddTreeMenu from './add-tree-menu'

/**
 * @extends Nav
 */
const FormsNav = Nav.extend(/** @lends FormsNav.prototype */{

	section: 'forms',
	model: Form,
	Gardener: FormsGardener,
	folders: true,

	events: _.extend({}, Nav.prototype.events, NewTab.events),

	isEditable: function(orgId) {
		return permissions.forms(orgId)
	},

	_defaultTransientLabel: function(attributes) {
		return i18n.get('common.new2', 'forms.' + attributes.type)
	},

	toggleEditViewState: function(edit, opts={}) {
		Nav.prototype.toggleEditViewState.call(this, edit)
		this.gardener.trigger('change:editViewState', edit, opts)

		if (edit) {
			// The tree actions behave different here than in other places.
			// See Issue #54625 for details.
			this.treeActions = new TreeActions({
				el: this.gardener.el,
				tree: this.gardener.tree,
				buttonContent: i18n.get('common.add'),
				extraClass: 'has-add-buttons has-full-opacity-buttons',
				disableTreeSelection: true
			}).render()

			// If the tree hasn't been rendered yet, wait for it it and call the
			// treeActions.render method so the correct button content is set.
			this.gardener.on('rendered', () => this.treeActions.render())

			this.treeActions.on('buttonContentSet', node => {
				this.onButtonContentSet(node)
			})

			this.treeActions.on('nodeSelected', node => {
				this.onButtonContentSet(node)
				this.onNodeSelected(node)
			})

			this.treeActions.on('selected', nodeId => {
				this.showAddTooltip(nodeId)
			})

			// #57628 remove the listener before adding it again
			this.off('add:transient')
				.on('add:transient', () => this.treeActions.hideActions())
		} else {
			this.treeActions?.hideActions()
		}
	},

	/*
	 * Set's the necessary classes on the tree node label-masks to display
	 * the add button to the the correct form node.
	 *
	 * The .is-selected-as-parent class is applied to the form node
	 * when a user hovers over/clicks its child node.
	 *
	 * The .is-chosen class is used when a user deliberately clicks
	 * on a node to select it.
	 */
	onButtonContentSet: function(node) {
		// The .is-selected-as-parent class is applied to the form node
		// when a user hovers over/clicks its child node.
		//
		// The .is-chosen class is used when a user deliberately clicks
		// on a node to select it.
		let data = this.gardener.tree.get(node.data('id'))
		if (data.type != FORM) {
			// Hide the button and allow that area to be clicked to
			// select the node but not trigger the tooltip.
			node.find('.label-mask').addClass('has-no-tree-action-button')
			if (data.parentId) {
				let parentFolderNodeId = this.getParentFormId(data.parentId)
				let parentNode = this.$(`.node[data-id=${parentFolderNodeId}]`)
				// If the parent node doesn't have a button yet, we need
				// to add it before we can show it.
				if (!parentNode.find('.label-mask').attr('data-button')) {
					this.treeActions.setButtonContent(null, parentNode, true)
				}
				parentNode.find('.label-mask:not(.is-chosen)')
					.addClass('is-selected-as-parent')
			}
		} else {
			node.find('.label-mask').addClass('is-selected-as-parent')
		}
	},

	/*
	 * Clears the treeActions and applies the button to the correct node
	 */
	resetTreeActions: function() {
		this.treeActions?.clear()
		this.treeActions?.setButtonContent()
	},

	/*
	 * Return the id of the parent form node's ID using a nested node's parentId
	 */
	getParentFormId: function(parentId) {
		let data = this.gardener.tree.get(parentId)

		// If direct parent is a form, return the form node.
		if (data?.type === FORM) {
			return parentId
		// Otherwise, see if the parent of the parent is a folder and return
		// that node if it is.
		} else if (data?.parentId) {
			let parentData = this.gardener.tree.get(data.parentId)
			if (parentData.type === FORM) {
				return parentData.id
			}
		}
	},

	/*
	 * Removes all .is-selected-as-parent and .is-chosen classes
	 * and reapplies them where necessary.
	 */
	onNodeSelected: function(node) {
		// If a child node is selected, and the parent node is collapsed so that
		// child node is no longer visible, we need to store it so the button
		// remains visible on the parent.
		this.selectedNode = node.length ? node : this.selectedNode
		let data = this.gardener.tree.get(this.selectedNode?.data('id'))
		this.$('.label-mask')
			.removeClass('is-selected-as-parent is-chosen')
		if (data?.parentId) {
			let parentFolderNodeId = this.getParentFormId(data.parentId)
			let parentNode = this.$(`.node[data-id=${parentFolderNodeId}]`)
			parentNode?.find('.label-mask:not(.has-no-tree-action-button)')
				.addClass('is-selected-as-parent is-chosen')
		}

		// If a folder is clicked, none of the nodes should have
		// a button attached.
		if (data.type == FOLDER) {
			this.$('.is-selected-as-parent')
				.removeClass('is-selected-as-parent')
		}
	},

	/** Shows the tooltip used to add pages and navigation tabs */
	showAddTooltip: function(formId) {
		if (!this.options.toggleEdit && !this.options.toggleEditViewState) {
			return
		}
		this.addTreeMenu?.remove()
		this.addTreeMenu = new AddTreeMenu({ formId })
		this.addTreeMenu.render()
		this.addTooltip?.remove()
		this.addTooltip = new Tooltip({
			placement: Tooltip.RIGHT,
			arrow: Tooltip.TOP,
			attachTo: this.$(`.node[data-id=${formId}]`),
			content: this.addTreeMenu.el,
			extraClass: 'tree-tooltip nav-pane-tree-tooltip'
		})
		this.addTooltip.render()
	},

	_addDomainObject: function(model, relativeId) {
		Nav.prototype._addDomainObject.apply(this, arguments)

		this.resetTreeActions()

		// If the actions were previously hidden because a transient was
		// visible. Make sure they are visible again.
		this.treeActions?.showActions()

		if (!this._interested(model)) {
			return
		}

		if (this._wrongOrg(model)) {
			return
		}

		// after adding a page, switch the URL to /edit
		if (_.endsWith(Backbone.history.fragment, 'new-page')) {
			conductor.navigate(conductor.editUrl(this.section, model.id))
		}
	},

	_deleteDomainObject: function() {
		Nav.prototype._deleteDomainObject.apply(this, arguments)
		this.resetTreeActions()
	},

	autoRemove: true

})

_.extend(FormsNav.prototype, _.omit(NewTab, ['events']))

export default FormsNav
FormsNav.__filename__ = 'forms/forms-nav.js'
